import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  ArrowLeft,
  ClipboardList,
  Loader2,
  Package,
  RefreshCw,
  ShoppingCart,
} from 'lucide-react';
import { toast } from 'sonner';
import { useBranch } from '../contexts/BranchContext';
import { workOrderApiService, WorkOrder } from '../services/workOrderApi';
import { bomHeaderApiService, BomHeader } from '../services/bomHeaderApi';
import { bomDetailApiService, BomDetail } from '../services/bomDetailApi';
import { materialIssueApiService } from '../services/materialIssueApi';
import { purchaseOrderApiService } from '../services/purchaseOrderApi';
import {
  aggregateMaterialRemaining,
  buildStoreMaterialRows,
  getWorkOrderEndgoodId,
  StoreMaterialRow,
  StoreMaterialStatus,
} from '../utils/workOrderMaterialUtils';

const statusConfig: Record<
  StoreMaterialStatus,
  { label: string; className: string }
> = {
  sufficient: {
    label: 'Sufficient',
    className: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  },
  shortfall: {
    label: 'Shortfall',
    className: 'bg-red-100 text-red-800 border-red-200',
  },
  not_issued: {
    label: 'Not Issued',
    className: 'bg-amber-100 text-amber-800 border-amber-200',
  },
};

const resolveBomForWorkOrder = async (workOrder: WorkOrder): Promise<BomHeader | null> => {
  if (workOrder.bomHeader) return workOrder.bomHeader;

  if (workOrder.bomHeaderId) {
    return bomHeaderApiService.getBomHeaderById(String(workOrder.bomHeaderId));
  }

  const endgoodId = getWorkOrderEndgoodId(workOrder);
  if (!endgoodId) return null;

  const headers = await bomHeaderApiService.getBomHeadersByEndgood(Number(endgoodId));
  return (
    headers.find((header) => header.isActive) ||
    [...headers].sort((a, b) => (b.id ?? 0) - (a.id ?? 0))[0] ||
    null
  );
};

const StoreWorkOrderDetailPage: React.FC = () => {
  const { workOrderId } = useParams<{ workOrderId: string }>();
  const navigate = useNavigate();
  const { selectedBranch } = useBranch();

  const [workOrder, setWorkOrder] = useState<WorkOrder | null>(null);
  const [bomDetails, setBomDetails] = useState<BomDetail[]>([]);
  const [materialRows, setMaterialRows] = useState<StoreMaterialRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [creatingPoForRowId, setCreatingPoForRowId] = useState<number | null>(null);

  const loadData = useCallback(async () => {
    if (!workOrderId) return;

    try {
      setLoading(true);
      const response = await workOrderApiService.getWorkOrderById(workOrderId);
      if (!response.success || !response.data) {
        toast.error('Work order not found');
        setWorkOrder(null);
        setBomDetails([]);
        setMaterialRows([]);
        return;
      }

      const wo = response.data;
      setWorkOrder(wo);

      const bom = await resolveBomForWorkOrder(wo);
      let details: BomDetail[] = [];
      if (bom) {
        details = await bomDetailApiService.getBomDetailsByHeader(bom.id);
      }
      setBomDetails(details);

      const materialResponse = await materialIssueApiService.getMaterialIssuesByWorkOrder(
        workOrderId,
        1,
        1000
      );
      const issues =
        materialResponse.success && materialResponse.data ? materialResponse.data : [];
      const remainingByKey = aggregateMaterialRemaining(issues);
      const lotQty = wo.remainingQuantity ?? wo.quantity ?? 0;

      setMaterialRows(buildStoreMaterialRows(details, remainingByKey, lotQty));
    } catch {
      toast.error('Failed to load work order material details');
      setWorkOrder(null);
      setBomDetails([]);
      setMaterialRows([]);
    } finally {
      setLoading(false);
    }
  }, [workOrderId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const summary = useMemo(() => {
    const shortfallCount = materialRows.filter((row) => row.status !== 'sufficient').length;
    const purchasableShortfall = materialRows.filter(
      (row) => row.isPurchasable && row.shortfall > 0
    ).length;
    return { shortfallCount, purchasableShortfall };
  }, [materialRows]);

  const handleCreatePo = async (row: StoreMaterialRow) => {
    if (!row.rawItemId || row.shortfall <= 0) return;

    try {
      setCreatingPoForRowId(row.id);

      const branchId = selectedBranch
        ? Number(selectedBranch)
        : workOrder?.branchId
          ? Number(workOrder.branchId)
          : undefined;

      const suggested = await purchaseOrderApiService.getSuggestedPrice(row.rawItemId, branchId);
      const purchaseRate = suggested?.unitPrice ?? 0;

      navigate('/purchase-orders/new', {
        state: {
          prefillItems: [
            {
              rawItemId: row.rawItemId,
              itemName: row.partName,
              quantity: row.shortfall,
              unit: row.unit,
              purchaseRate,
              priceSource: suggested?.source,
              pricePoNumber: suggested?.poNumber,
            },
          ],
          workOrderNumber: workOrder?.workOrderNumber,
        },
      });
    } catch {
      toast.error('Failed to prepare purchase order');
    } finally {
      setCreatingPoForRowId(null);
    }
  };

  const endgoodName =
    workOrder?.endgoodName || workOrder?.endgood?.endgoodName || 'Unknown endgood';

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <Card className="border-primary-200 bg-white shadow-lg">
          <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <Button
                variant="outline"
                size="icon"
                onClick={() => navigate('/dashboard/store')}
                className="shrink-0"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div>
                <CardTitle className="text-xl font-bold text-gray-900">
                  {workOrder?.workOrderNumber || 'Work Order Materials'}
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  {endgoodName}
                  {workOrder ? ` · Lot ${workOrder.remainingQuantity ?? workOrder.quantity} PCS` : ''}
                  {selectedBranch ? ` · Branch ${selectedBranch}` : ''}
                </p>
              </div>
            </div>
            <Button variant="outline" onClick={loadData} disabled={loading}>
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              <span className="ml-2">Refresh</span>
            </Button>
          </CardHeader>
        </Card>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="border-primary-200 shadow-sm">
            <CardContent className="pt-5">
              <p className="text-xs text-muted-foreground">BOM Lines</p>
              <p className="text-2xl font-bold text-gray-900">{bomDetails.length}</p>
            </CardContent>
          </Card>
          <Card className="border-primary-200 shadow-sm">
            <CardContent className="pt-5">
              <p className="text-xs text-muted-foreground">Raw Material Lines</p>
              <p className="text-2xl font-bold text-gray-900">{materialRows.length}</p>
            </CardContent>
          </Card>
          <Card className="border-primary-200 shadow-sm">
            <CardContent className="pt-5">
              <p className="text-xs text-muted-foreground">Need Attention</p>
              <p className="text-2xl font-bold text-red-600">{summary.shortfallCount}</p>
            </CardContent>
          </Card>
        </div>

        <Card className="border-primary-200 bg-white shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-primary-600" />
              Material Requirements by Process
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Quantity needed is based on BOM per-unit qty × remaining work order quantity.
              Remaining is net issued material (issued − consumed − returned − wasted).
            </p>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
              </div>
            ) : !workOrder ? (
              <div className="text-center py-16 text-muted-foreground">
                Work order not found.
              </div>
            ) : materialRows.length === 0 ? (
              <div className="text-center py-16">
                <Package className="h-12 w-12 text-primary-300 mx-auto mb-3" />
                <p className="text-gray-700 font-medium">No raw material lines found</p>
                <p className="text-sm text-muted-foreground mt-1">
                  This work order has no purchasable raw items in its BOM.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Process Name</TableHead>
                      <TableHead>Part Name (Raw Item)</TableHead>
                      <TableHead className="text-right">Qty Remaining</TableHead>
                      <TableHead className="text-right">Qty Needed</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {materialRows.map((row) => {
                      const status = statusConfig[row.status];
                      const canCreatePo = row.isPurchasable && row.shortfall > 0;

                      return (
                        <TableRow key={row.id}>
                          <TableCell className="font-medium">{row.processName}</TableCell>
                          <TableCell>{row.partName}</TableCell>
                          <TableCell className="text-right">
                            {row.quantityRemaining} {row.unit}
                          </TableCell>
                          <TableCell className="text-right">
                            {row.quantityNeeded} {row.unit}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={status.className}>
                              {status.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            {canCreatePo ? (
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-primary-300 text-primary-700 hover:bg-primary-50"
                                disabled={creatingPoForRowId === row.id}
                                onClick={() => handleCreatePo(row)}
                              >
                                {creatingPoForRowId === row.id ? (
                                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                                ) : (
                                  <ShoppingCart className="h-3.5 w-3.5 mr-1.5" />
                                )}
                                Create PO
                              </Button>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default StoreWorkOrderDetailPage;
